import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { NavigationStep, RmiCategory } from '../types'
import * as api from '../api/tecdocApi'
import type { CustomRmiLink } from '../api/tecdocApi'
import type { ApiError } from '../types/api'

export const useNavigationStore = defineStore('navigation', () => {
  // Breadcrumb
  const navigationPath = ref<NavigationStep[]>([])
  const currentLevel = ref<'vehicle' | 'mainGroup' | 'subGroup' | 'products'>('vehicle')

  // SVG content at each level
  const vehicleSvg = ref<string | null>(null)
  const mainGroupSvg = ref<string | null>(null)
  const subGroupSvg = ref<string | null>(null)

  // Current IDs
  const mainGroupId = ref<string | null>(null)
  const subGroupId = ref<string | null>(null)
  const itemId = ref<string | null>(null)

  // Category menu
  const rawCategories = ref<RmiCategory[]>([])
  const categories = ref<RmiCategory[]>([])

  // Config from admin
  const hiddenIds = ref<string[]>([])
  const hiddenItems = ref<{ id: string; mode: string }[]>([])
  const hideProductsNotInStore = ref(false)
  const categoryNames = ref<Record<string, string>>({})
  const pinnedManufacturers = ref<string[]>([])
  const canEditCategories = ref(false)
  const customRmiLinks = ref<CustomRmiLink[]>([])
  let adminConfigLoaded = false

  // Loading/error
  const isLoadingSvg = ref(false)
  const navigationError = ref<string | null>(null)

  const breadcrumbs = computed(() => navigationPath.value)

  async function loadAdminConfig() {
    if (adminConfigLoaded) return
    try {
      const config = await api.getTecdocConfig()
      hiddenItems.value = Array.isArray(config.hiddenItems) ? config.hiddenItems : []
      hiddenIds.value = hiddenItems.value.map(h => h?.id).filter(Boolean) as string[]
      hideProductsNotInStore.value = !!config.hideProductsNotInStore
      categoryNames.value = config.categoryNames ?? {}
      pinnedManufacturers.value = Array.isArray(config.pinnedManufacturers) ? config.pinnedManufacturers : []
      canEditCategories.value = !!config.canEditCategories
      customRmiLinks.value = Array.isArray(config.customRmiLinks) ? config.customRmiLinks : []
      adminConfigLoaded = true
    } catch {
      // keep defaults on failure
    }
  }

  function getDisplayName(key: string, fallback: string): string {
    const override = categoryNames.value[key]
    return override && override.length > 0 ? override : fallback
  }

  async function updateCategoryName(key: string, name: string | null) {
    try {
      const result = await api.saveCategoryName(key, name)
      categoryNames.value = result.categoryNames ?? {}
    } catch (err) {
      // swallow — caller can inspect if needed
      throw err
    }
  }

  async function addCustomRmiLink(
    categoryId: number,
    label: string,
    mg: string | null,
    sg: string | null,
    iconCode: string | null,
  ) {
    const updated = await api.saveCustomRmiLink({
      categoryId,
      label,
      sortOrder: 0,
      mainGroupId: mg,
      subGroupId: sg,
      iconCode,
    })
    customRmiLinks.value = Array.isArray(updated) ? updated : []
  }

  async function removeCustomRmiLink(
    categoryId: number,
    mg: string | null,
    sg: string | null,
  ) {
    const updated = await api.deleteCustomRmiLink(categoryId, mg, sg)
    customRmiLinks.value = Array.isArray(updated) ? updated : []
  }

  // Normalize scope values: treat null / undefined / '' as "unscoped" (null).
  function normScope(v: string | null | undefined): string | null {
    return v && String(v).trim().length > 0 ? String(v).trim() : null
  }

  // The subset of custom links that should be visible right now, given
  // the current (mainGroupId, subGroupId) pair in the navigation store.
  // Exact match — a link pinned at mg=1,sg=null only shows when mg=1 AND sg=null.
  const customRmiLinksForCurrentScope = computed<CustomRmiLink[]>(() => {
    const curMg = normScope(mainGroupId.value)
    const curSg = normScope(subGroupId.value)
    return customRmiLinks.value.filter(l => {
      return normScope(l.mainGroupId) === curMg && normScope(l.subGroupId) === curSg
    })
  })

  async function loadVehicleImage(qualIdVal: string, carId: number) {
    isLoadingSvg.value = true
    navigationError.value = null

    try {
      // Fetch admin config (once) in parallel with the vehicle image.
      const [result] = await Promise.all([
        api.getRmiVehicleImage(qualIdVal, carId),
        loadAdminConfig(),
      ])

      vehicleSvg.value = result.svgContent

      // Store raw categories; editors see everything, others see filtered
      rawCategories.value = result.categories
      categories.value = canEditCategories.value
        ? result.categories
        : filterCategories(result.categories, hiddenIds.value)
      currentLevel.value = 'vehicle'
      navigationPath.value = [{
        level: 'vehicle',
        id: String(carId),
        name: 'Kjøretøy',
      }]
    } catch (err) {
      navigationError.value = (err as ApiError).message
    } finally {
      isLoadingSvg.value = false
    }
  }

  async function navigateToMainGroup(qualIdVal: string, carId: number, mgId: string, mgName: string) {
    isLoadingSvg.value = true
    navigationError.value = null
    mainGroupId.value = mgId

    try {
      const result = await api.getRmiMainGroupImage(qualIdVal, carId, mgId)
      mainGroupSvg.value = result.svgContent
      currentLevel.value = 'mainGroup'

      // Update breadcrumb — keep vehicle, add main group
      navigationPath.value = [
        navigationPath.value[0],
        { level: 'mainGroup', id: mgId, name: mgName },
      ]
    } catch (err) {
      navigationError.value = (err as ApiError).message
    } finally {
      isLoadingSvg.value = false
    }
  }

  async function navigateToSubGroup(qualIdVal: string, carId: number, mgId: string, sgId: string, sgName: string) {
    isLoadingSvg.value = true
    navigationError.value = null
    subGroupId.value = sgId

    try {
      const result = await api.getRmiSubGroupImage(qualIdVal, carId, mgId, sgId)
      if (result.error) {
        navigationError.value = 'Bilde ikke tilgjengelig for denne gruppen'
        return
      }
      subGroupSvg.value = result.svgContent
      currentLevel.value = 'subGroup'

      // Update breadcrumb — keep vehicle + main group, add sub group
      navigationPath.value = [
        navigationPath.value[0],
        navigationPath.value[1],
        { level: 'subGroup', id: sgId, name: sgName },
      ]
    } catch (err) {
      navigationError.value = (err as ApiError).message
    } finally {
      isLoadingSvg.value = false
    }
  }

  function setProductsLevel(itemIdVal: string, itemName: string) {
    itemId.value = itemIdVal
    currentLevel.value = 'products'

    navigationPath.value = [
      navigationPath.value[0],
      navigationPath.value[1],
      navigationPath.value[2],
      { level: 'products', id: itemIdVal, name: itemName },
    ]
  }

  function navigateBack() {
    if (navigationPath.value.length <= 1) return

    navigationPath.value = navigationPath.value.slice(0, -1)
    const last = navigationPath.value[navigationPath.value.length - 1]
    currentLevel.value = last.level

    // Clear state for levels below
    if (last.level === 'vehicle') {
      mainGroupId.value = null
      mainGroupSvg.value = null
      subGroupId.value = null
      subGroupSvg.value = null
      itemId.value = null
    } else if (last.level === 'mainGroup') {
      subGroupId.value = null
      subGroupSvg.value = null
      itemId.value = null
    } else if (last.level === 'subGroup') {
      itemId.value = null
    }
  }

  function navigateToLevel(level: NavigationStep['level']) {
    const idx = navigationPath.value.findIndex(s => s.level === level)
    if (idx < 0) return

    navigationPath.value = navigationPath.value.slice(0, idx + 1)
    currentLevel.value = level
    navigateBack() // triggers cleanup
    // Re-add the level since navigateBack pops
    currentLevel.value = level
  }

  async function hideCategory(id: string, label: string, mode: 'whitebox' | 'opacity') {
    const result = await api.saveHiddenCategory(id, label, mode)
    hiddenItems.value = result.hiddenItems
    hiddenIds.value = result.hiddenItems.map(h => h.id)
    // Non-editors get filtered; editors keep full list
    if (!canEditCategories.value) {
      categories.value = filterCategories(rawCategories.value, hiddenIds.value)
    }
  }

  async function unhideCategory(id: string) {
    const result = await api.deleteHiddenCategory(id)
    hiddenItems.value = result.hiddenItems
    hiddenIds.value = result.hiddenItems.map(h => h.id)
    if (!canEditCategories.value) {
      categories.value = filterCategories(rawCategories.value, hiddenIds.value)
    }
  }

  function getHideModeForCategory(id: string): 'whitebox' | 'opacity' | null {
    const item = hiddenItems.value.find(h => h.id === id)
    return item ? (item.mode as 'whitebox' | 'opacity') : null
  }

  function filterCategories(cats: RmiCategory[], hidden: string[]): RmiCategory[] {
    if (!hidden || !hidden.length) return cats
    return cats
      .filter(mg => !hidden.includes(`mg:${mg.mainGroupId}`))
      .map(mg => ({
        ...mg,
        subGroups: mg.subGroups
          ?.filter(sg => !hidden.includes(`sg:${mg.mainGroupId}:${sg.subGroupId}`))
          .map(sg => ({
            ...sg,
            items: sg.items?.filter(item =>
              !hidden.includes(`item:${mg.mainGroupId}:${sg.subGroupId}:${item.itemMpId}`)
            ) ?? [],
          })) ?? [],
      }))
  }

  function reset() {
    navigationPath.value = []
    currentLevel.value = 'vehicle'
    vehicleSvg.value = null
    mainGroupSvg.value = null
    subGroupSvg.value = null
    mainGroupId.value = null
    subGroupId.value = null
    itemId.value = null
    categories.value = []
    navigationError.value = null
    // Admin config (hidden ids / pinned mfrs / canEdit / category renames) is
    // kept across vehicle changes so the sidebar rename overrides persist.
  }

  return {
    navigationPath,
    currentLevel,
    vehicleSvg,
    mainGroupSvg,
    subGroupSvg,
    mainGroupId,
    subGroupId,
    itemId,
    categories,
    hiddenIds,
    hiddenItems,
    hideProductsNotInStore,
    categoryNames,
    pinnedManufacturers,
    canEditCategories,
    customRmiLinks,
    customRmiLinksForCurrentScope,
    addCustomRmiLink,
    removeCustomRmiLink,
    hideCategory,
    unhideCategory,
    getHideModeForCategory,
    isLoadingSvg,
    navigationError,
    breadcrumbs,
    loadVehicleImage,
    loadAdminConfig,
    getDisplayName,
    updateCategoryName,
    navigateToMainGroup,
    navigateToSubGroup,
    setProductsLevel,
    navigateBack,
    navigateToLevel,
    reset,
  }
})
